import { MenuSubItem } from "@/components/header-group/components/MenuSubItem"
import { processMenuUrl } from "@/components/header-group/utils"
import { StructuredMenu } from "@/components/header-one/menu-structure"
import { LanguageLabel } from "@/components/language-label"
import { languages } from "@/configs"
import { Images } from "@/constants/images"
import { getLocaleHomeHref } from "@/lib/locale-utils"
import { toImageSrc } from "@/lib/utils"
import { CaretDownOutlined } from "@ant-design/icons"
import Image from "next/image"
import Link from "next/link"
import { memo } from "react"

type HeaderDesktopProps = {
  locale: string
  structuredMenu: StructuredMenu
  activeItemId: number | null
  localeLinks: Record<string, string>
  headerLogoUrl?: string
}

export const HeaderDesktop = memo(
  ({ locale, structuredMenu, activeItemId, localeLinks, headerLogoUrl }: HeaderDesktopProps) => {
    const homeHref = getLocaleHomeHref(locale)
    // Logo fetch sẵn ở server (HeaderOne) và truyền xuống → render ngay, hết nháy.
    const logoUrl = headerLogoUrl ? toImageSrc(headerLogoUrl) : Images.logoTamdaGroup

    return (
      <div
        className="fixed top-0 left-0 right-0 bg-white shadow-2xl hidden lg:block z-50"
        style={{ top: "var(--admin-bar-h, 0px)" }}
      >
        <div className="flex flex-col gap-3">
          <div className="max-w-full w-[1290px] mx-auto flex justify-between h-[74px] items-center">
            <div className="sm:w-[20%] px-[15px] image-hover">
              <div className="w-[180px] h-[17px] relative">
                <Link href={homeHref}>
                  <Image alt="Logo" src={logoUrl} fill className="object-contain" priority />
                </Link>
              </div>
            </div>
            <div className="sm:w-[67.3%] px-[15px]">
              <nav className="relative">
                <ul className="flex justify-center gap-10 h-[70px]">
                  {structuredMenu?.map(item => {
                    const hasSubmenu = item.attribute && item.attribute.length > 0
                    const { finalUrl, isExternal } = processMenuUrl(item.url, locale)

                    const linkClassName = `relative flex gap-[6px] text-[15px] leading-[22px] font-bold items-center text-[#242f3e] ${
                      activeItemId === item.id ? "text-[#18af4e]" : ""
                    }`

                    // Gạch chân hover nằm ở đáy header (trên <li> full height), không dính sát chữ
                    const itemClassName = `relative group flex items-center after:block after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[3px] after:bg-[#18af4e] after:scale-x-0 after:transition-transform after:duration-500 after:origin-left hover:after:scale-x-100 ${
                      activeItemId === item.id ? " after:scale-x-100" : ""
                    }`

                    return (
                      <li key={item.id} className={itemClassName}>
                        <Link
                          href={finalUrl}
                          className={linkClassName}
                          target={isExternal ? "_blank" : undefined}
                          rel={isExternal ? "noopener noreferrer" : undefined}
                          prefetch={isExternal ? false : true}
                        >
                          {item.title}
                          {hasSubmenu && <CaretDownOutlined className="text-[10px] leading-4 " />}
                        </Link>
                        {hasSubmenu && item.attribute && (
                          <ul className="hidden absolute py-5 font-normal group-hover:block top-full left-0 bg-white z-50 duration-300 outline-none border-none text-nowrap pointer-events-none group-hover:pointer-events-auto group-hover:shadow-lg">
                            {item.attribute.map((subItem, index) => (
                              <li key={index}>
                                <MenuSubItem
                                  subItem={subItem}
                                  locale={locale}
                                  className="block text-[#242f3e] text-[15px] leading-[31px] font-bold px-5 sm:py-2 hover:bg-gray-100 hover:text-[#18af4e] transition-all duration-300"
                                />
                              </li>
                            ))}
                          </ul>
                        )}
                      </li>
                    )
                  })}
                </ul>
              </nav>
            </div>
            <div className="sm:w-[12.7%] px-[15px] h-[70px] flex items-center justify-end">
              <div className="relative group h-full flex items-center justify-center cursor-pointer after:block after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[3px] after:bg-[#18af4e] after:scale-x-0 after:transition-transform after:duration-500 after:origin-left hover:after:scale-x-100">
                <div className="cursor-pointer">
                  {(() => {
                    const currentLang = languages.find(lang => lang.code === locale) || languages[1]
                    return (
                      <LanguageLabel
                        language={currentLang.shortLabel}
                        urlLogo={currentLang.logo}
                        checkShow={true}
                        showLogo
                      />
                    )
                  })()}
                </div>
                <ul
                  className="absolute py-2.5 right-0 top-[70px] bg-white shadow-2xl z-50 min-w-[250px] flex flex-col opacity-0 translate-y-4 pointer-events-none transition-all duration-500 ease-out group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto"
                  aria-hidden="true"
                >
                  {languages.map(lang => (
                    <li key={lang.code}>
                      <Link
                        href={localeLinks[lang.code] ?? lang.href}
                        className="flex items-center px-5 py-2.5 hover:bg-gray-100 hover:text-[#18af4e] transition-all duration-500"
                      >
                        <LanguageLabel language={lang.label} urlLogo={lang.logo} checkShow={false} showIcon={false} />
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  },
)
